import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BasePackageProductCard from "../../../../components/Card/BasePackageProductCard";
import GlobalButton from "../../../../components/CustomButton";
import BaseProductCardSkeleton from "../../../../components/Skeleton/BasePackageCardSkeleton";
import { getAllSupplierProducts } from "../../../../services/supplier/productApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { BasePackageSelectionDialog } from "./SelectionDialog";

const LIMIT = 20;

export const Products = ({
    inputValues,
    selectedProducts,
    setSelectedProducts,
}) => {
    const { t } = useTranslation();
    const [allProducts, setAllProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const dialogRef = useRef(null);
    const userData = useSelector((state) => state.userData.user);

    const fetchAllProducts = async (page = 1, replace = false) => {
        try {
            if (page === 1) setIsLoading(true);
            else setIsLoadingMore(true);

            const params = {
                solutionType: inputValues.solutionType,
                categoryId: inputValues.category,
                subCategoryId: inputValues.subCategory,
                subSubCategoryId: inputValues.subSubCategory,
                page: page,
                limit: LIMIT,
                supplierId: userData?._id,
            };

            const response = await getAllSupplierProducts(params);
            if (response.data.status === "success") {
                const products = response.data.data.data;
                const pagination = response.data.data.pagination;
                setAllProducts((prev) =>
                    replace ? products : [...prev, ...products],
                );
                setHasMore(pagination.hasMore);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        if (inputValues.category && inputValues.solutionType?.length > 0) {
            setCurrentPage(1);
            fetchAllProducts(1, true);
        }
    }, [
        inputValues.category,
        inputValues.subCategory,
        inputValues.subSubCategory,
        inputValues.solutionType?.length,
    ]);

    useEffect(() => {
        setAllProducts([]);
    }, [inputValues.category, inputValues.solutionType?.length]);

    const selectedIds = useMemo(
        () => new Set(selectedProducts.map((p) => p._id)),
        [selectedProducts],
    );

    const handleQuickView = (data, isAdded) => {
        dialogRef.current.openDialog({
            product: data,
            isAdded: isAdded,
        });
    };

    const handleLoadMore = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchAllProducts(nextPage);
    };

    return (
        <Box mt={3}>
            <Typography
                variant="h6"
                fontWeight={600}
                color="text.primary"
                gutterBottom
            >
                {t("supplierProductTab.allProducts")}
            </Typography>
            {allProducts?.length === 0 && !isLoading && (
                <Box
                    mt={3}
                    p={3}
                    borderRadius={2}
                    bgcolor="grey.50"
                    border="1px dashed"
                    borderColor="grey.300"
                    textAlign="center"
                >
                    {!inputValues.category || !inputValues.solutionType?.length ? (
                        <>
                            <FilterListIcon
                                sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                                aria-hidden
                            />
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                color="text.primary"
                                gutterBottom
                            >
                                {t("supplierProductTab.selectFiltersToFetchProducts")}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                maxWidth={900}
                                mx="auto"
                            >
                                {t("supplierProductTab.selectFiltersToFetchProductsSub")}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            {t("common.productNotFound")}.
                        </Typography>
                    )}
                </Box>
            )}

            <Grid container spacing={2} mt={2}>
                {isLoading
                    ? Array.from({ length: 8 }).map((_, index) => (
                        <Grid item xs={12} sm={4} md={2.4} key={index}>
                            <BaseProductCardSkeleton />
                        </Grid>
                    ))
                    : allProducts?.map((item) => (
                        <Grid item xs={6} sm={4} md={2.4} key={item._id}>
                            <BasePackageProductCard
                                cardData={item}
                                colorName="text.black"
                                handleQuickView={handleQuickView}
                                isAlreadyAdded={selectedIds.has(item._id)}
                            />
                        </Grid>
                    ))}
            </Grid>

            {hasMore && !isLoading && (
                <Box mt={3} textAlign="center">
                    <GlobalButton
                        variant="contained"
                        handleClickBtn={handleLoadMore}
                        disabled={isLoadingMore}
                        btnText={isLoadingMore ? t("common.loading") : t("common.loadMore")}
                        styleProps={{ width: "auto", height: 30, borderRadius: 1 }}
                    />
                </Box>
            )}

            <BasePackageSelectionDialog
                ref={dialogRef}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
            />
        </Box>
    );
};

